/**
 * Focus helpers for popover/menu components that want to hand focus back
 * to the natural tab order when the user Tabs past the popover's content.
 */

const TABBABLE_SELECTOR =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Returns every tabbable element in the document in document order,
 * filtering out hidden or off-screen nodes.
 */
const collectTabbable = (): HTMLElement[] =>
  Array.from(document.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter(
    (el) => {
      if (el.getAttribute("aria-hidden") === "true") return false;
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    },
  );

/**
 * Focuses the next tabbable element after `anchor` in document order.
 * Backwards direction is honoured when `direction === "backward"` (for
 * Shift+Tab). No-ops if there's nothing to focus.
 */
export const focusAdjacent = (
  anchor: HTMLElement | null,
  direction: "forward" | "backward" = "forward",
): void => {
  if (!anchor) return;
  const focusables = collectTabbable();
  const idx = focusables.indexOf(anchor);
  if (idx === -1) return;
  const nextIdx = direction === "forward" ? idx + 1 : idx - 1;
  const next = focusables[nextIdx];
  next?.focus();
};
